/**
 * ConsultaDetallePage — Vista de detalle de una consulta finalizada.
 *
 * Muestra TODA la información persistida:
 *   - Datos del paciente y consulta
 *   - Resumen de cierre (disposición, plan, acciones realizadas, comentario)
 *   - SOAP final firmado (notas_medicas/final)
 *   - Hallazgos clínicos FHIR (firmados en notas_medicas/final; en vivo, del documento único `actual`)
 *   - Alertas + educación al paciente
 *   - Notas post-consulta (timeline cronológico, tipo nota_post_consulta y nota_doctor_vivo)
 *   - Transcript completo (transcript/completo)
 *   - Comentario del médico al cerrar
 *
 * Si la consulta es del médico autenticado Y aún está dentro de la ventana
 * de 24h, muestra prominente el botón "Agregar nota" + modal de captura.
 *
 * Acceso: médico que atendió, admin, supervisor (vía Firestore rules).
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileSignature, MessageSquareText,
  Pill, Microscope, Activity, BookOpen,
  Plus, Clock, AlertTriangle, User, Calendar,
  CheckCircle2, FileText, Gauge, RefreshCw, Pencil, Save, X,
  Mic, Download, Play, Copy, MapPin, TextSelect,
} from 'lucide-react';
import { doc, collection, onSnapshot, orderBy, query, type Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { agregarNotaPostConsulta } from '../services/consultaService';
import { fixMojibake } from '../utils/fixMojibake';
import { normalizarSoap } from '../utils/soapNormalize';
import MainLayout from '../components/layout/MainLayout';
import { Modal, Button } from '../components/ui';
import { Avatar } from '../components/ui/Avatar';
import { DISPOSICION_LABELS, DISPOSICION_DESCRIPCIONES, type Consulta, type DisposicionConsulta } from '../types';
import { NotaSoape } from '../components/livekit/NotaSoape';
import { StructuredPlan } from '../components/livekit/StructuredPlan';
import type { FHIRItem, SOAPData } from '../services/medicalAI';
import { copiarSoape, copiarSeleccionActual } from '../utils/copiarSoape';
import '../styles/notaClinica.css';
import styles from './ConsultaDetallePage.module.css';

const VENTANA_NOTAS_HORAS = 24;

/* La nota del historial usa los MISMOS componentes que la consulta en vivo
 * (NotaSoape + StructuredPlan). Aquí no hay ciclo de IA ni marcas de autoría
 * en curso, así que esos canales van vacíos — constantes de módulo para no
 * crear objetos nuevos en cada render. */
const SIN_PROPUESTAS: Partial<SOAPData> = {};
const SIN_MARCAS: Set<keyof SOAPData> = new Set();
const noop = () => {};

interface SoapFinalDoc {
  s?: string;
  o?: string;
  a?: string;
  p?: string;
  e?: string; // SOAPE: Evolución (respuesta del paciente al tratamiento)
  /** NU: el FHIR (medicamentos/diagnósticos/órdenes) se firma junto al SOAPE. */
  fhir?: Array<Record<string, unknown>>;
  healthEducation?: string;
  alerts?: string[];
  firmadoPor?: { uid: string; nombre: string; role: string };
  firmadoEn?: Timestamp;
}

interface NotaMedica {
  id: string;
  tipo: string;
  soap?: { subjective?: string; objective?: string; assessment?: string; plan?: string; evolution?: string; education?: string };
  summary?: string;
  confidence?: number;
  fhir?: Array<Record<string, unknown>>;
  alerts?: string[];
  healthEducation?: string | string[];
  texto?: string;
  autor?: { uid: string; nombre: string; role: string };
  medicoId?: string;
  creadoEn?: Timestamp;
}

interface TranscriptSegment {
  id: string;
  speaker: string;
  text: string;
  ts: number;
  isFinal: boolean;
}

interface TranscriptDoc {
  segments: TranscriptSegment[];
  duracionSegundos?: number;
  guardadoEn?: Timestamp;
}

// M5 — Análisis de sentimientos (auditoría)
interface SentimientoLado {
  sentimiento?: string;
  puntuacion?: number;
  nota?: string;
  resumen?: string;
}
interface SentimientoDoc {
  general?: SentimientoLado;
  paciente?: SentimientoLado;
  medico?: SentimientoLado;
  alertas?: string[];
  modelo?: string;
  analizadoPor?: string;
  analizadoEn?: Timestamp;
  segmentos?: number;
}

/** Etiqueta + color para un sentimiento. */
function sentimientoMeta(s: string | undefined): { label: string; color: string; bg: string } {
  switch ((s || '').toLowerCase()) {
    case 'positivo': return { label: 'Positivo', color: '#0b7a3e', bg: '#e7f5ee' };
    case 'negativo': return { label: 'Negativo', color: '#c0392b', bg: '#fdecea' };
    case 'mixto':    return { label: 'Mixto',    color: '#b7791f', bg: '#fdf6e3' };
    case 'neutral':  return { label: 'Neutral',  color: '#1f5f8b', bg: '#e8f1f8' };
    default:         return { label: s || '—',   color: '#5b6b63', bg: '#eef1ef' };
  }
}

function formatTimestamp(t: Timestamp | undefined | null): string {
  if (!t) return '—';
  try {
    return t.toDate().toLocaleString('es-PA', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatDuration(seconds: number | undefined): string {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function planSeguimientoLabel(plan: string | undefined): string {
  const map: Record<string, string> = {
    ninguno: 'Sin seguimiento',
    '1_semana': 'Seguimiento en 1 semana',
    '2_semanas': 'Seguimiento en 2 semanas',
    '1_mes': 'Seguimiento en 1 mes',
    especialista: 'Referido a especialista',
    urgencias: 'Atención de urgencias',
  };
  return plan ? (map[plan] ?? plan) : '—';
}

// Etiqueta + descripción del cierre desde los mapas centrales (types/index.ts)
// — antes había un mapa local incompleto (sin cancelada_paciente ni
// paciente_rechazo) que dejaba pasar el código interno crudo a la UI.
function disposicionLabel(d: string | undefined): string {
  if (!d) return '—';
  return DISPOSICION_LABELS[d as DisposicionConsulta] ?? d;
}

function disposicionDescripcion(d: string | undefined): string | null {
  if (!d) return null;
  return DISPOSICION_DESCRIPCIONES[d as DisposicionConsulta] ?? null;
}

function formatSexo(sexo?: string): string {
  if (!sexo) return '—';
  const s = sexo.trim().toLowerCase();
  if (s === 'm' || s.startsWith('masc') || s === 'hombre') return 'Masculino';
  if (s === 'f' || s.startsWith('fem') || s === 'mujer') return 'Femenino';
  return sexo;
}

function formatEdad(fechaNacimiento?: string, edadDoc?: number): string {
  if (typeof edadDoc === 'number' && edadDoc > 0) return `${edadDoc} años`;
  if (!fechaNacimiento) return '—';
  const nac = new Date(fechaNacimiento);
  if (Number.isNaN(nac.getTime())) return '—';
  const hoy = new Date();
  let e = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) e--;
  return e >= 0 && e < 130 ? `${e} años` : '—';
}

export default function ConsultaDetallePage() {
  const { consultaId } = useParams<{ consultaId: string }>();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { addToast } = useToast();

  const [consulta, setConsulta] = useState<Consulta | null>(null);
  const [soapFinal, setSoapFinal] = useState<SoapFinalDoc | null>(null);
  const [soapeCopiado, setSoapeCopiado] = useState(false);
  const copiadoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [seleccionEstado, setSeleccionEstado] = useState<'copiado' | 'vacio' | null>(null);
  const seleccionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [notas, setNotas] = useState<NotaMedica[]>([]);
  const [transcript, setTranscript] = useState<TranscriptDoc | null>(null);
  const [sentimiento, setSentimiento] = useState<SentimientoDoc | null>(null);
  const [analizandoSent, setAnalizandoSent] = useState(false);
  const [sentimientoError, setSentimientoError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNotaModal, setShowNotaModal] = useState(false);
  const [notaTexto, setNotaTexto] = useState('');
  const [notaSubmitting, setNotaSubmitting] = useState(false);

  // Edición de la nota clínica desde el historial (mismo día) + bitácora
  const [editMode, setEditMode] = useState(false);
  const [editSoap, setEditSoap] = useState({ s: '', o: '', a: '', p: '', e: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Grabación de audio de la teleconsulta (URL firmada on-demand).
  // Modo tracks (TrackEgress): un archivo por participante → `grabArchivos`.
  // Modo legacy (RoomComposite): un solo archivo mezclado → `grabUrl`.
  const [grabUrl, setGrabUrl] = useState<string | null>(null);
  const [grabArchivos, setGrabArchivos] = useState<
    Array<{ rol: string; url: string; duracionSeg: number }> | null
  >(null);
  /** Archivo único mezclado (médico izquierda, paciente derecha). Preferido. */
  const [grabMezcla, setGrabMezcla] = useState<
    { url: string; duracionSeg: number; formato?: string } | null
  >(null);
  const [grabCargando, setGrabCargando] = useState(false);
  const [grabError, setGrabError] = useState<string | null>(null);

  // Datos de identidad del paciente (PII descifrada): cédula, sexo, nacimiento.
  // `consulta.pacienteId` es el ID interno, NO la cédula → se obtiene del paciente.
  const [pacienteInfo, setPacienteInfo] = useState<
    { cedula?: string; sexo?: string; fechaNacimiento?: string; fotoURL?: string | null } | null
  >(null);
  // Fallback demográfico del gateway MINSA (edad/sexo) para pacientes de
  // teleconsulta sin registro en el portal (issue #3).
  const [pdpInfo, setPdpInfo] = useState<{ caracterSexo?: string; fechaNacimiento?: string } | null>(null);

  const cargarGrabacion = useCallback(async () => {
    if (!consultaId || grabUrl || grabCargando) return;
    setGrabCargando(true);
    setGrabError(null);
    try {
      const fn = httpsCallable<
        { consultaId: string },
        {
          url: string;
          mezcla?: { url: string; duracionSeg: number; formato?: string } | null;
          archivos?: Array<{ rol: string; url: string; duracionSeg: number }>;
        }
      >(functions, 'portal_obtenerUrlGrabacion');
      const res = await fn({ consultaId });
      if (res.data.mezcla) setGrabMezcla(res.data.mezcla);
      if (res.data.archivos?.length) setGrabArchivos(res.data.archivos);
      setGrabUrl(res.data.url);
    } catch (err) {
      setGrabError(err instanceof Error ? err.message : 'No se pudo obtener la grabación.');
    } finally {
      setGrabCargando(false);
    }
  }, [consultaId, grabUrl, grabCargando]);

  // Obtener identidad real del paciente (cédula/sexo/nacimiento descifrados).
  useEffect(() => {
    const pid = consulta?.pacienteId;
    if (!pid) { setPacienteInfo(null); return; }
    let cancel = false;
    const fn = httpsCallable<
      { pacienteId: string; pacienteUid?: string },
      { paciente?: { cedula?: string; sexo?: string; fechaNacimiento?: string; fotoURL?: string | null } }
    >(functions, 'portal_obtenerPaciente');
    // pacienteUid permite a la CF resolver la foto de perfil (users/{uid}) para
    // consultas viejas que no tienen pacienteFotoURL denormalizado.
    fn({ pacienteId: pid, pacienteUid: consulta?.pacienteUid })
      .then((res) => { if (!cancel) setPacienteInfo(res.data?.paciente ?? null); })
      .catch(() => { if (!cancel) setPacienteInfo(null); });
    return () => { cancel = true; };
  }, [consulta?.pacienteId, consulta?.pacienteUid]);

  // Fallback: edad/sexo desde el gateway MINSA (mismo endpoint que la app móvil).
  useEffect(() => {
    const pid = consulta?.pacienteId;
    if (!pid) { setPdpInfo(null); return; }
    let cancel = false;
    (async () => {
      try {
        const idToken = await auth.currentUser?.getIdToken();
        const fn = httpsCallable<
          { pacienteId: string; pacienteUid?: string; idToken?: string; consultaId?: string },
          { paciente?: { caracterSexo?: string; fechaNacimiento?: string } | null }
        >(functions, 'portal_obtenerPacientePdp');
        const res = await fn({ pacienteId: pid, pacienteUid: consulta?.pacienteUid, idToken, consultaId });
        if (!cancel && res.data?.paciente) setPdpInfo(res.data.paciente);
      } catch { if (!cancel) setPdpInfo(null); }
    })();
    return () => { cancel = true; };
  }, [consulta?.pacienteId, consulta?.pacienteUid, consultaId]);

  // Suscribirse al doc principal
  useEffect(() => {
    if (!consultaId) return;
    const unsub = onSnapshot(
      doc(db, 'consultas', consultaId),
      (snap) => {
        setConsulta(snap.exists() ? ({ id: snap.id, ...snap.data() } as Consulta) : null);
        setLoading(false);
      },
      (err) => {
        console.error('[ConsultaDetalle] error consulta:', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [consultaId]);

  // Suscribirse a notas_medicas/final
  useEffect(() => {
    if (!consultaId) return;
    const unsub = onSnapshot(
      doc(db, `consultas/${consultaId}/notas_medicas/final`),
      (snap) => setSoapFinal(snap.exists() ? (snap.data() as SoapFinalDoc) : null),
    );
    return () => unsub();
  }, [consultaId]);

  // Suscribirse a todas las notas_medicas (timeline ordenado)
  useEffect(() => {
    if (!consultaId) return;
    const q = query(
      collection(db, `consultas/${consultaId}/notas_medicas`),
      orderBy('creadoEn', 'asc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs
        .filter((d) => d.id !== 'final')  // 'final' va por separado
        .map((d) => ({ id: d.id, ...d.data() } as NotaMedica));
      // El documento único `actual` no tiene creadoEn ordenable como las
      // notas de texto; se conserva en la lista (se busca por id) pero no
      // participa del timeline de notas del médico.
      setNotas(items);
    });
    return () => unsub();
  }, [consultaId]);

  // Cargar transcript completo (one-time, no es live)
  useEffect(() => {
    if (!consultaId) return;
    const unsub = onSnapshot(
      doc(db, `consultas/${consultaId}/transcript/completo`),
      (snap) => setTranscript(snap.exists() ? (snap.data() as TranscriptDoc) : null),
    );
    return () => unsub();
  }, [consultaId]);

  // M5 — Análisis de sentimientos (se genera solo al finalizar; aquí solo lo leemos)
  useEffect(() => {
    if (!consultaId) return;
    const unsub = onSnapshot(
      doc(db, `consultas/${consultaId}/analisis_sentimiento/resultado`),
      (snap) => { setSentimiento(snap.exists() ? (snap.data() as SentimientoDoc) : null); setSentimientoError(null); },
      (err) => {
        console.error('[ConsultaDetalle] error sentimiento:', err);
        setSentimientoError('No se pudo leer el análisis (permisos o conexión).');
      },
    );
    return () => unsub();
  }, [consultaId]);

  // Re-analizar a demanda (callable). El snapshot de arriba refresca el panel.
  const handleAnalizarSentimiento = useCallback(async () => {
    if (!consultaId || analizandoSent) return;
    setAnalizandoSent(true);
    setSentimientoError(null);
    try {
      const fn = httpsCallable<{ consultaId: string }, { success: boolean }>(functions, 'portal_analizarSentimiento');
      await fn({ consultaId });
      addToast('success', 'Análisis de sentimientos actualizado');
    } catch (err) {
      console.error('[ConsultaDetalle] analizarSentimiento error:', err);
      const msg = err instanceof Error ? err.message : 'No se pudo analizar';
      setSentimientoError(msg);
      addToast('error', msg);
    } finally {
      setAnalizandoSent(false);
    }
  }, [consultaId, analizandoSent, addToast]);

  // Calcular ventana de notas
  const ventana = useMemo(() => {
    if (!consulta?.finalizadaEn) return { puedeAgregar: false, horasRestantes: 0 };
    const fin = (consulta.finalizadaEn as Timestamp | Date | undefined);
    const finMs = fin instanceof Date
      ? fin.getTime()
      : (fin as Timestamp | undefined)?.toMillis?.();
    if (!finMs) return { puedeAgregar: false, horasRestantes: 0 };
    const horasTranscurridas = (Date.now() - finMs) / (60 * 60 * 1000);
    const horasRestantes = Math.max(0, VENTANA_NOTAS_HORAS - horasTranscurridas);
    const esMedicoAutor = role === 'medico' && consulta.medicoId === user?.uid;
    return {
      puedeAgregar: esMedicoAutor && horasRestantes > 0,
      horasRestantes: Math.floor(horasRestantes),
    };
  }, [consulta, role, user?.uid]);

  // ¿La consulta es del MISMO DÍA (zona Panamá)? Solo entonces se puede editar.
  const esMismoDia = useMemo(() => {
    const ref = (consulta?.finalizadaEn ?? consulta?.iniciadaEn ?? consulta?.creadoEn) as Timestamp | undefined;
    const d = ref?.toDate?.();
    if (!d) return false;
    const fmt = (x: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Panama' }).format(x);
    return fmt(d) === fmt(new Date());
  }, [consulta]);

  /** Una consulta CERRADA es inmutable: su nota quedó firmada. Lo que ocurra
   *  después se registra como nota de observación, que nunca altera el SOAPE.
   *  Por eso desaparecen «Editar nota» y «Abrir en consulta» — ofrecerlos sería
   *  prometer una edición que el backend rechaza. */
  const consultaCerrada =
    consulta?.estado === 'finalizada' || consulta?.estado === 'cancelada';

  const puedeEditar = !consultaCerrada && esMismoDia && (
    (role === 'medico' && consulta?.medicoId === user?.uid) ||
    role === 'admin' || role === 'supervisor'
  );

  const handleGuardarEdicion = useCallback(async () => {
    if (!consultaId || savingEdit) return;
    setSavingEdit(true);
    try {
      const fn = httpsCallable<{ consultaId: string; soap: typeof editSoap; origen: string }, { cambios?: number }>(
        functions, 'portal_editarConsultaHistorial',
      );
      const res = await fn({ consultaId, soap: editSoap, origen: 'historial' });
      const n = res.data?.cambios ?? 0;
      addToast('success', n > 0 ? `Cambios guardados (${n}). Quedó registrado en la bitácora.` : 'No hubo cambios que guardar.');
      navigate('/');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'No se pudo guardar los cambios');
    } finally {
      setSavingEdit(false);
    }
  }, [consultaId, savingEdit, editSoap, addToast, navigate]);

  // NU: el FHIR firmado viene directo de la nota final (o del documento único
  // `actual` si la consulta sigue abierta) — sin reconstrucción ni dedup local.
  const notaActual = useMemo(
    () => notas.find((n) => n.id === 'actual'),
    [notas],
  );
  const fhirConsolidado = useMemo(() => {
    const fuente = (soapFinal?.fhir?.length ? soapFinal.fhir : notaActual?.fhir) ?? [];
    return fuente as Array<Record<string, unknown>>;
  }, [soapFinal, notaActual]);

  // Alertas: las de la nota (final o actual).
  const alertasConsolidadas = useMemo(() => {
    const fuente = (soapFinal?.alerts?.length ? soapFinal.alerts : notaActual?.alerts) ?? [];
    return fuente.filter((a) => typeof a === 'string' && a.trim());
  }, [soapFinal, notaActual]);

  // Educación al paciente: la de la nota (final o actual).
  const educacionConsolidada = useMemo(() => {
    const e = soapFinal?.healthEducation ?? notaActual?.healthEducation;
    return Array.isArray(e) ? e.join('\n') : (typeof e === 'string' ? e.trim() : '');
  }, [soapFinal, notaActual]);

  // Notas escritas por el médico (en vivo + post-consulta)
  const notasMedicoTexto = useMemo(() => {
    return notas
      .filter((n) => n.tipo === 'nota_post_consulta' || n.tipo === 'nota_doctor_vivo')
      .sort((a, b) => {
        const ta = (a.creadoEn as Timestamp | undefined)?.toMillis?.() ?? 0;
        const tb = (b.creadoEn as Timestamp | undefined)?.toMillis?.() ?? 0;
        return ta - tb;
      });
  }, [notas]);

  const handleSubmitNota = useCallback(async () => {
    if (!consultaId || !notaTexto.trim() || notaSubmitting) return;
    setNotaSubmitting(true);
    try {
      const res = await agregarNotaPostConsulta(consultaId, notaTexto.trim());
      addToast('success', `Nota agregada. Quedan ${res.horasRestantes}h para más notas.`);
      setShowNotaModal(false);
      setNotaTexto('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al agregar nota';
      addToast('error', msg);
    } finally {
      setNotaSubmitting(false);
    }
  }, [consultaId, notaTexto, notaSubmitting, addToast]);

  if (!consultaId) {
    return <MainLayout pageTitle="Detalle"><p>No se proporcionó consultaId.</p></MainLayout>;
  }

  if (loading) {
    return (
      <MainLayout pageTitle="Detalle de consulta">
        <div className={styles.loading}>
          <Activity size={32} className={styles.spinner} />
          <p>Cargando consulta...</p>
        </div>
      </MainLayout>
    );
  }

  if (!consulta) {
    return (
      <MainLayout pageTitle="Detalle de consulta">
        <div className={styles.notFound}>
          <AlertTriangle size={36} />
          <h2>Consulta no encontrada</h2>
          <Button onClick={() => navigate('/historial')}>
            <ArrowLeft size={14} /> Volver al historial
          </Button>
        </div>
      </MainLayout>
    );
  }

  // SOAP a mostrar: priorizar 'final' firmado; si la consulta sigue abierta,
  // el documento único `actual` (que ya guarda s/o/a/p/e directamente).
  const soap = soapFinal
    ? (() => {
        const n = normalizarSoap({
          subjective: soapFinal.s, objective: soapFinal.o,
          assessment: soapFinal.a, plan: soapFinal.p, evolution: soapFinal.e,
        });
        return {
          s: n?.subjective ?? soapFinal.s, o: n?.objective ?? soapFinal.o,
          a: n?.assessment ?? soapFinal.a, p: n?.plan ?? soapFinal.p,
          e: n?.evolution ?? soapFinal.e,
        };
      })()
    : (() => {
        const s = notaActual?.soap as
          | { s?: string; o?: string; a?: string; p?: string; e?: string }
          | undefined;
        if (!s) return null;
        return { s: s.s, o: s.o, a: s.a, p: s.p, e: s.e };
      })();

  const hasSoap = !!(soap && (soap.s || soap.o || soap.a || soap.p || soap.e));
  const soapVista: SOAPData = {
    s: soap?.s ?? '', o: soap?.o ?? '', a: soap?.a ?? '', p: soap?.p ?? '', e: soap?.e ?? '',
  };
  const resumenPaciente = (consulta as Consulta & { resumenPaciente?: string }).resumenPaciente ?? '';

  const handleCopiarSoape = async () => {
    if (!(await copiarSoape(editMode ? editSoap : soapVista))) return;
    setSoapeCopiado(true);
    if (copiadoTimerRef.current) clearTimeout(copiadoTimerRef.current);
    copiadoTimerRef.current = setTimeout(() => setSoapeCopiado(false), 1800);
  };
  const handleCopiarSeleccion = async () => {
    const ok = await copiarSeleccionActual();
    setSeleccionEstado(ok ? 'copiado' : 'vacio');
    if (seleccionTimerRef.current) clearTimeout(seleccionTimerRef.current);
    seleccionTimerRef.current = setTimeout(() => setSeleccionEstado(null), 1800);
  };
  const transcriptSegments = transcript?.segments ?? [];

  return (
    <MainLayout pageTitle="Detalle de consulta" onBack={() => navigate('/historial')}>
      <div className={styles.page}>
        {/* Header — solo acciones (el botón regresar va junto al título) */}
        <div className={styles.header} style={{ justifyContent: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {editMode ? (
              <>
                <Button variant="secondary" onClick={() => setEditMode(false)} disabled={savingEdit}>
                  <X size={14} aria-hidden="true" /> Cancelar
                </Button>
                <Button variant="primary" onClick={handleGuardarEdicion} loading={savingEdit}>
                  <Save size={14} aria-hidden="true" /> Guardar cambios y volver al inicio
                </Button>
              </>
            ) : (
              <>
                {puedeEditar && (
                  <>
                    <button
                      type="button"
                      className={styles.addNoteBtn}
                      onClick={() => navigate(`/consulta/${consultaId}/video-ai?revision=1`)}
                      title="Abrir la consulta completa (videollamada deshabilitada)"
                    >
                      <FileSignature size={14} aria-hidden="true" /> Abrir en consulta
                    </button>
                    <button
                      type="button"
                      className={styles.addNoteBtn}
                      onClick={() => {
                        setEditSoap({ s: soap?.s ?? '', o: soap?.o ?? '', a: soap?.a ?? '', p: soap?.p ?? '', e: soap?.e ?? '' });
                        setEditMode(true);
                      }}
                      title="Editar la nota clínica (consulta del mismo día)"
                    >
                      <Pencil size={14} aria-hidden="true" /> Editar nota
                    </button>
                  </>
                )}
                {ventana.puedeAgregar && (
                  <button
                    type="button"
                    className={styles.addNoteBtn}
                    onClick={() => setShowNotaModal(true)}
                  >
                    <Plus size={14} aria-hidden="true" /> Agregar nota
                    <span className={styles.timerBadge}>
                      <Clock size={11} aria-hidden="true" /> {ventana.horasRestantes}h restantes
                    </span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {editMode && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
            background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10,
            color: '#9a3412', fontSize: 13.5,
          }}>
            <Pencil size={15} aria-hidden="true" />
            <span>Editando la nota clínica. Los cambios quedarán registrados en la bitácora de auditoría.</span>
          </div>
        )}

        {/* Patient header */}
        <section className={styles.patientCard}>
          <div className={styles.patientLeft}>
            <Avatar
              className={styles.patientAvatar}
              nombre={fixMojibake(consulta.pacienteNombre)}
              fotoURL={consulta.pacienteFotoURL ?? pacienteInfo?.fotoURL}
              zoomable
            />
            <div>
              <h1 className={styles.patientName}>{fixMojibake(consulta.pacienteNombre) || 'Paciente'}</h1>
              <p className={styles.patientMeta}>
                Cédula: <strong>{pacienteInfo?.cedula || '—'}</strong>
                {' · '}Edad: <strong>{formatEdad(pacienteInfo?.fechaNacimiento ?? pdpInfo?.fechaNacimiento, (consulta as Consulta & { pacienteEdad?: number }).pacienteEdad)}</strong>
                {' · '}Género: <strong>{formatSexo(pacienteInfo?.sexo ?? pdpInfo?.caracterSexo ?? (consulta as Consulta & { pacienteSexo?: string }).pacienteSexo)}</strong>
                {' · '}Sala: <strong>{consulta.salaNombre || '—'}</strong>
                {/* Va en esta línea y no en la rejilla de la derecha: allí
                    añadía una fila entera y estiraba el encabezado. */}
                {consulta.pacienteUbicacion && (
                  <>
                    {' · '}
                    <a
                      className={styles.ubicacionLink}
                      href={`https://www.google.com/maps?q=${consulta.pacienteUbicacion.lat},${consulta.pacienteUbicacion.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      title={`Ubicación al solicitar la consulta · ${consulta.pacienteUbicacion.lat.toFixed(5)}, ${consulta.pacienteUbicacion.lng.toFixed(5)}`
                        + (consulta.pacienteUbicacion.precisionM != null
                          ? ` · precisión ±${consulta.pacienteUbicacion.precisionM} m`
                          : '')}
                    >
                      <MapPin size={11} aria-hidden="true" /> Ubicación
                    </a>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className={styles.patientRight}>
            <div className={styles.metaItem}>
              <Calendar size={12} aria-hidden="true" />
              <span className={styles.metaLabel}>Atendida</span>
              <span className={styles.metaValue}>{formatTimestamp(consulta.iniciadaEn as Timestamp | undefined)}</span>
            </div>
            <div className={styles.metaItem}>
              <CheckCircle2 size={12} aria-hidden="true" />
              <span className={styles.metaLabel}>Finalizada</span>
              <span className={styles.metaValue}>{formatTimestamp(consulta.finalizadaEn as Timestamp | undefined)}</span>
            </div>
            <div className={styles.metaItem}>
              <Activity size={12} aria-hidden="true" />
              <span className={styles.metaLabel}>Duración</span>
              <span className={styles.metaValue}>{formatDuration(consulta.duracionSegundos as number | undefined)}</span>
            </div>
            <div className={styles.metaItem}>
              <User size={12} aria-hidden="true" />
              <span className={styles.metaLabel}>Atendió</span>
              <span className={styles.metaValue}>{consulta.medicoNombre || '—'}</span>
            </div>
          </div>
        </section>

        {/* Cita en HCIS — estado de la creación de la cita al tomar al paciente */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <Calendar size={16} aria-hidden="true" /> Cita en HCIS (MINSA)
          </h2>
          {(() => {
            const cita = consulta.citaSeis;
            const parr: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 };
            if (!cita) {
              return <p style={parr}>No se registró información de la cita en HCIS para esta consulta.</p>;
            }
            const toMs = (d: unknown): number => {
              const t = d as { toMillis?: () => number; seconds?: number } | null;
              if (!t) return 0;
              if (typeof t.toMillis === 'function') return t.toMillis();
              if (typeof t.seconds === 'number') return t.seconds * 1000;
              return 0;
            };
            const ms = toMs(cita.enviadaEn ?? cita.actualizadaEn);
            const cuando = ms > 0 ? new Date(ms).toLocaleString('es-PA') : null;
            if (cita.estado === 'enviada') {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ color: 'var(--color-success, #15803d)', fontWeight: 600, fontSize: 13 }}>
                    ✓ Cita confirmada en HCIS{cuando ? ` — ${cuando}` : ''}
                  </span>
                  {(cita.fechaSolicitada || cita.horaSolicitada) && (
                    <span style={parr}>
                      Cupo: <strong>{cita.fechaSolicitada}</strong>
                      {cita.horaSolicitada ? <> a las <strong>{cita.horaSolicitada}</strong></> : null}
                    </span>
                  )}
                </div>
              );
            }
            if (cita.estado === 'no_enviada_deshabilitada') {
              return (
                <p style={parr}>
                  La cita <strong>no se envió</strong> porque el envío a HCIS está deshabilitado en la configuración.
                </p>
              );
            }
            return (
              <p style={parr}>
                <span style={{ color: 'var(--color-danger, #b91c1c)', fontWeight: 600 }}>
                  ✕ La cita no se pudo crear en HCIS.
                </span>
                {cita.error ? <> {cita.error}</> : null}
                {cuando ? <> (último intento: {cuando})</> : null}
                {cita.incierto ? <> No se sabe si HCIS llegó a registrarla.</> : null}
              </p>
            );
          })()}
        </section>

        {/* Grabación de audio de la consulta */}
        {(consulta as Consulta & { grabacionCompletada?: boolean; grabacionDuracionSeg?: number }).grabacionCompletada && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}><Mic size={16} aria-hidden="true" /> Grabación de la consulta</h2>
            {!grabUrl ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={styles.addNoteBtn}
                  onClick={cargarGrabacion}
                  disabled={grabCargando}
                >
                  {grabCargando
                    ? <><RefreshCw size={14} className={styles.spinner} aria-hidden="true" /> Cargando…</>
                    : <><Play size={14} aria-hidden="true" /> Cargar grabación</>}
                </button>
                {typeof (consulta as Consulta & { grabacionDuracionSeg?: number }).grabacionDuracionSeg === 'number' && (
                  <span style={{ fontSize: 12.5, color: '#5b6b63' }}>
                    Duración: {formatDuration((consulta as Consulta & { grabacionDuracionSeg?: number }).grabacionDuracionSeg)}
                  </span>
                )}
              </div>
            ) : grabMezcla ? (
              /* Consulta completa en un solo archivo. La separación de
                 hablantes no se pierde: vive en los canales, médico a la
                 izquierda y paciente a la derecha. Las pistas sueltas quedan
                 detrás de un desplegable, para el caso raro en que haga falta
                 aislar a uno de los dos. */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: '#3d4a44' }}>
                  Consulta completa
                  {grabMezcla.duracionSeg > 0 && (
                    <span style={{ fontWeight: 400, color: '#5b6b63' }}> · {formatDuration(grabMezcla.duracionSeg)}</span>
                  )}
                </span>
                <audio controls src={grabMezcla.url} style={{ width: '100%' }} />
                <span style={{ fontSize: 11.5, color: '#5b6b63' }}>
                  Audio estéreo: el médico se escucha por el canal izquierdo y el paciente por el derecho.
                </span>
                <a
                  href={grabMezcla.url}
                  download={`grabacion-${consultaId}.ogg`}
                  className={styles.addNoteBtn}
                  style={{ alignSelf: 'flex-start', textDecoration: 'none' }}
                >
                  <Download size={14} aria-hidden="true" /> Descargar audio
                </a>
                {grabArchivos && grabArchivos.length > 0 && (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12.5, color: '#5b6b63' }}>
                      Pistas por participante
                    </summary>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                      {grabArchivos.map((a) => (
                        <div key={a.rol + a.url.slice(-16)} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#3d4a44' }}>
                            {a.rol === 'medico' ? 'Médico' : a.rol === 'paciente' ? 'Paciente' : a.rol === 'supervisor' ? 'Supervisor' : 'Participante'}
                          </span>
                          <audio controls src={a.url} style={{ width: '100%' }} />
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ) : grabArchivos ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {grabArchivos.map((a) => {
                  const rolLabel =
                    a.rol === 'medico' ? 'Médico'
                    : a.rol === 'paciente' ? 'Paciente'
                    : a.rol === 'supervisor' ? 'Supervisor'
                    : 'Participante';
                  return (
                    <div key={a.rol + a.url.slice(-16)} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: '#3d4a44' }}>
                        {rolLabel}
                        {a.duracionSeg > 0 && (
                          <span style={{ fontWeight: 400, color: '#5b6b63' }}> · {formatDuration(a.duracionSeg)}</span>
                        )}
                      </span>
                      <audio controls src={a.url} style={{ width: '100%' }} />
                      <a
                        href={a.url}
                        download={`grabacion-${consultaId}-${a.rol}.ogg`}
                        className={styles.addNoteBtn}
                        style={{ alignSelf: 'flex-start', textDecoration: 'none' }}
                      >
                        <Download size={14} aria-hidden="true" /> Descargar audio ({rolLabel.toLowerCase()})
                      </a>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <audio controls src={grabUrl} style={{ width: '100%' }} />
                <a
                  href={grabUrl}
                  download={`grabacion-${consultaId}.ogg`}
                  className={styles.addNoteBtn}
                  style={{ alignSelf: 'flex-start', textDecoration: 'none' }}
                >
                  <Download size={14} aria-hidden="true" /> Descargar audio
                </a>
              </div>
            )}
            {grabError && (
              <p style={{ color: '#b42318', fontSize: 13, marginTop: 8 }}>{grabError}</p>
            )}
          </section>
        )}

        {/* Motivo */}
        {consulta.motivoConsulta && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}><FileText size={16} aria-hidden="true" /> Motivo de consulta</h2>
            <p className={styles.motivoText}>{consulta.motivoConsulta}</p>
          </section>
        )}

        {/* Resumen de cierre */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}><FileSignature size={16} aria-hidden="true" /> Resumen de cierre</h2>
          <div className={styles.closureGrid}>
            <div className={styles.closureItem}>
              <span className={styles.closureLabel}>Disposición</span>
              <span className={styles.closureValue}>{disposicionLabel(consulta.disposicion as string | undefined)}</span>
              {disposicionDescripcion(consulta.disposicion as string | undefined) && (
                <span className={styles.closureDesc}>
                  {disposicionDescripcion(consulta.disposicion as string | undefined)}
                </span>
              )}
            </div>
            <div className={styles.closureItem}>
              <span className={styles.closureLabel}>Plan de seguimiento</span>
              <span className={styles.closureValue}>{planSeguimientoLabel((consulta as Consulta & { planSeguimiento?: string }).planSeguimiento)}</span>
            </div>
            {((consulta as Consulta & { diagnosticoPrincipal?: { codigo: string; descripcion: string } }).diagnosticoPrincipal) && (
              <div className={styles.closureItemWide}>
                <span className={styles.closureLabel}>Diagnóstico principal</span>
                <span className={styles.closureValue}>
                  {((consulta as Consulta & { diagnosticoPrincipal?: { codigo: string; descripcion: string } }).diagnosticoPrincipal?.codigo) && (
                    <code className={styles.code}>{(consulta as Consulta & { diagnosticoPrincipal?: { codigo: string; descripcion: string } }).diagnosticoPrincipal?.codigo}</code>
                  )}
                  {' '}
                  {(consulta as Consulta & { diagnosticoPrincipal?: { codigo: string; descripcion: string } }).diagnosticoPrincipal?.descripcion}
                </span>
              </div>
            )}
            <div className={styles.closureItemFull}>
              <span className={styles.closureLabel}>Acciones realizadas</span>
              <div className={styles.actionTags}>
                {((consulta as Consulta & { recetaEmitida?: boolean }).recetaEmitida) && (
                  <span className={styles.actionTag}><Pill size={11} /> Receta emitida</span>
                )}
                {((consulta as Consulta & { ordenLabEmitida?: boolean }).ordenLabEmitida) && (
                  <span className={styles.actionTag}><Microscope size={11} /> Orden de laboratorio</span>
                )}
                {((consulta as Consulta & { educacionEntregada?: boolean }).educacionEntregada) && (
                  <span className={styles.actionTag}><BookOpen size={11} /> Educación entregada</span>
                )}
                {!((consulta as Consulta & { recetaEmitida?: boolean }).recetaEmitida) &&
                  !((consulta as Consulta & { ordenLabEmitida?: boolean }).ordenLabEmitida) &&
                  !((consulta as Consulta & { educacionEntregada?: boolean }).educacionEntregada) && (
                  <span className={styles.muted}>Sin acciones registradas</span>
                )}
              </div>
            </div>
            {((consulta as Consulta & { comentarioMedico?: string }).comentarioMedico) && (
              <div className={styles.closureItemFull}>
                <span className={styles.closureLabel}>Comentario del médico al cerrar</span>
                <p className={styles.closureComment}>{(consulta as Consulta & { comentarioMedico?: string }).comentarioMedico}</p>
              </div>
            )}
            {consulta.disposicion === 'cancelada_supervisor' && consulta.observacionesCierre && (
              <div className={styles.closureItemFull}>
                <span className={styles.closureLabel}>Motivo de la cancelación</span>
                <p className={styles.closureComment}>{consulta.observacionesCierre}</p>
              </div>
            )}
          </div>
        </section>

        {/* ── NOTA CLÍNICA ──────────────────────────────────────────────
            Se muestra con los MISMOS componentes que el médico usa durante la
            consulta (NotaSoape + StructuredPlan): un solo documento continuo
            —S·O·A·P·E, plan estructurado y resumen para el paciente— en vez de
            las cuatro secciones sueltas que había aquí (SOAP, hallazgos FHIR,
            alertas y educación). Lo que se firmó es lo que se lee. */}
        {(hasSoap || editMode || fhirConsolidado.length > 0) && (
          <section className={`tcTokens ${styles.notaSec}`}>
            <div className={styles.notaHead}>
              <span className={styles.notaEstado}>
                <span className={styles.notaEstadoDot} aria-hidden="true" />
                {soapFinal?.firmadoPor
                  ? `Nota firmada por ${soapFinal.firmadoPor.nombre}`
                  : soapFinal
                    ? 'Nota firmada'
                    : 'Nota en curso'}
              </span>
              {(hasSoap || editMode) && (
                <button
                  type="button"
                  className={`${styles.copiarSoape} ${soapeCopiado ? styles.copiarSoapeHecho : ''}`}
                  onClick={handleCopiarSoape}
                  title="Copiar la nota SOAPE completa al portapapeles"
                  aria-label="Copiar la nota SOAPE completa al portapapeles"
                >
                  {soapeCopiado
                    ? <><CheckCircle2 size={12} aria-hidden="true" /> Copiado</>
                    : <><Copy size={12} aria-hidden="true" /> Copiar SOAPE</>}
                </button>
              )}
              {(hasSoap || editMode) && (
                <button
                  type="button"
                  className={`${styles.copiarSoape} ${seleccionEstado === 'copiado' ? styles.copiarSoapeHecho : ''}`}
                  onClick={handleCopiarSeleccion}
                  title="Copiar solo el texto sombreado de una sección"
                  aria-label="Copiar solo el texto sombreado de una sección"
                >
                  {seleccionEstado === 'copiado'
                    ? <><CheckCircle2 size={12} aria-hidden="true" /> Copiado</>
                    : seleccionEstado === 'vacio'
                      ? <><TextSelect size={12} aria-hidden="true" /> Sombree texto primero</>
                      : <><TextSelect size={12} aria-hidden="true" /> Copiar selección</>}
                </button>
              )}
            </div>

            {alertasConsolidadas.length > 0 && (
              <aside className={styles.alertasNota}>
                <span className={styles.alertasNotaTag}>
                  <AlertTriangle size={11} aria-hidden="true" /> Alertas de la consulta
                </span>
                <ul className={styles.alertasNotaLista}>
                  {alertasConsolidadas.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </aside>
            )}

            <NotaSoape
              soap={editMode ? editSoap : soapVista}
              onChange={editMode
                ? (key, value) => setEditSoap((prev) => ({ ...prev, [key]: value }))
                : noop}
              pendingAIUpdates={SIN_PROPUESTAS}
              onAccept={noop}
              onDismiss={noop}
              recentlyUpdated={SIN_MARCAS}
              doctorEdited={SIN_MARCAS}
              readOnly={!editMode}
            />

            {(fhirConsolidado.length > 0 || educacionConsolidada) && (
              <StructuredPlan
                fhir={fhirConsolidado as unknown as FHIRItem[]}
                healthEducation={educacionConsolidada}
                planText={soapVista.p}
              />
            )}

            {resumenPaciente && (
              <>
                <div className={styles.corteResumen}>
                  <span className={styles.corteResumenEtiqueta}>Para el paciente</span>
                </div>
                <div className={styles.resumenPacSec}>
                  <span className={styles.resumenPacInsignia} aria-hidden="true">Rp</span>
                  <div className={styles.resumenPacCuerpo}>
                    <header className={styles.resumenPacHead}>
                      <h4 className={styles.resumenPacTitulo}>Resumen de su consulta</h4>
                      <span className={styles.resumenPacHint}>lo ve en su app</span>
                    </header>
                    <p className={styles.resumenPacTexto}>{resumenPaciente}</p>
                  </div>
                </div>
              </>
            )}
          </section>
        )}

        {/* Notas del médico (timeline) */}
        {notasMedicoTexto.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <MessageSquareText size={16} aria-hidden="true" /> Notas del médico ({notasMedicoTexto.length})
            </h2>
            <ol className={styles.notasTimeline}>
              {notasMedicoTexto.map((n) => (
                <li key={n.id} className={styles.notaItem}>
                  <div className={styles.notaHeader}>
                    <span className={`${styles.notaTipo} ${n.tipo === 'nota_doctor_vivo' ? styles.notaVivo : styles.notaPost}`}>
                      {n.tipo === 'nota_doctor_vivo' ? 'En vivo' : 'Post-consulta'}
                    </span>
                    <span className={styles.notaTime}>{formatTimestamp(n.creadoEn)}</span>
                    {n.autor?.nombre && <span className={styles.notaAutor}>· {n.autor.nombre}</span>}
                  </div>
                  <p className={styles.notaTexto}>{n.texto}</p>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Transcript */}
        {/* M5 — Análisis de sentimientos (auditoría). Se genera solo al
            finalizar; el botón permite re-analizar a demanda. */}
        {(transcriptSegments.length > 0 || sentimiento) && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Gauge size={16} aria-hidden="true" /> Análisis de sentimientos
                <span style={{ fontSize: 11, fontWeight: 600, color: '#5b6b63', background: '#eef1ef', borderRadius: 6, padding: '1px 7px' }}>auditoría</span>
              </span>
              {transcriptSegments.length > 0 && (
                <button
                  type="button"
                  onClick={handleAnalizarSentimiento}
                  disabled={analizandoSent}
                  title="Re-analizar el tono de la conversación con IA"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#1A8048', border: '1px solid #1A8048', borderRadius: 8, padding: '5px 12px', fontSize: 12.5, fontWeight: 600, cursor: analizandoSent ? 'default' : 'pointer', opacity: analizandoSent ? 0.6 : 1 }}
                >
                  <RefreshCw size={13} aria-hidden="true" /> {analizandoSent ? 'Analizando…' : (sentimiento ? 'Re-analizar' : 'Analizar')}
                </button>
              )}
            </h2>

            {sentimientoError && !analizandoSent ? (
              <p style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#c0392b', fontSize: 13.5, margin: 0 }}>
                <AlertTriangle size={14} aria-hidden="true" /> {sentimientoError}
              </p>
            ) : !sentimiento ? (
              <p className={styles.muted}>
                {analizandoSent
                  ? 'Analizando el tono de la conversación…'
                  : 'El análisis se genera automáticamente al finalizar la consulta. Si no aparece, usá «Analizar».'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Tono general */}
                {sentimiento.general && (() => {
                  const m = sentimientoMeta(sentimiento.general.sentimiento);
                  const p = sentimiento.general.puntuacion;
                  return (
                    <div style={{ background: m.bg, borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: '#5b6b63' }}>Tono general</span>
                        <span style={{ fontWeight: 800, color: m.color }}>{m.label}</span>
                        {typeof p === 'number' && (
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: m.color }}>
                            ({p > 0 ? '+' : ''}{p.toFixed(2)})
                          </span>
                        )}
                      </div>
                      {sentimiento.general.resumen && (
                        <p style={{ margin: '6px 0 0', color: '#2c3a33', fontSize: 14 }}>{sentimiento.general.resumen}</p>
                      )}
                    </div>
                  );
                })()}

                {/* Por interlocutor */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
                  {([['Médico', sentimiento.medico], ['Paciente', sentimiento.paciente]] as const).map(([titulo, lado]) => {
                    if (!lado) return null;
                    const m = sentimientoMeta(lado.sentimiento);
                    const p = lado.puntuacion;
                    return (
                      <div key={titulo} style={{ border: '1px solid #e2e8e4', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: '#1b2620' }}>{titulo}</span>
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: m.color, background: m.bg, borderRadius: 999, padding: '2px 9px' }}>{m.label}</span>
                          {typeof p === 'number' && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#5b6b63' }}>{p > 0 ? '+' : ''}{p.toFixed(2)}</span>
                          )}
                        </div>
                        {lado.nota && <p style={{ margin: '5px 0 0', color: '#5b6b63', fontSize: 13 }}>{lado.nota}</p>}
                      </div>
                    );
                  })}
                </div>

                {/* Alertas */}
                {Array.isArray(sentimiento.alertas) && sentimiento.alertas.length > 0 && (
                  <div style={{ background: '#fdecea', border: '1px solid #f5c6c0', borderRadius: 10, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, color: '#c0392b', fontWeight: 700, fontSize: 13.5 }}>
                      <AlertTriangle size={15} aria-hidden="true" /> Alertas de comunicación ({sentimiento.alertas.length})
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 20, color: '#2c3a33', fontSize: 14 }}>
                      {sentimiento.alertas.map((a, i) => <li key={i} style={{ margin: '2px 0' }}>{a}</li>)}
                    </ul>
                  </div>
                )}

                <p style={{ margin: 0, fontSize: 11.5, color: '#8a9690' }}>
                  Análisis automático del tono de la comunicación (no es un diagnóstico).
                  {sentimiento.analizadoEn && <> · {formatTimestamp(sentimiento.analizadoEn)}</>}
                  {sentimiento.modelo && <> · {sentimiento.modelo}</>}
                </p>
              </div>
            )}
          </section>
        )}

        {transcriptSegments.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <MessageSquareText size={16} aria-hidden="true" /> Conversación ({transcriptSegments.length} {transcriptSegments.length === 1 ? 'mensaje' : 'mensajes'})
            </h2>
            {/* Mismo formato que la transcripción en vivo de la consulta:
                mensajes consecutivos agrupados por interlocutor, fila completa,
                paciente con fondo tenue e indentado. */}
            <div className={styles.txThread}>
              {(() => {
                const fmtTime = (ts: number) => {
                  const d = new Date(ts);
                  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                };
                const groups: { isDoctor: boolean; label: string; texts: string[]; key: string; lastTs: number }[] = [];
                for (const seg of transcriptSegments) {
                  const isDoctor = seg.speaker === 'Doctor' || seg.speaker === 'Medico' || seg.speaker.startsWith('medico_');
                  const label = isDoctor
                    ? 'Doctor'
                    : (seg.speaker === 'Paciente' || seg.speaker.startsWith('paciente_') ? 'Paciente' : seg.speaker);
                  const last = groups[groups.length - 1];
                  if (last && last.isDoctor === isDoctor) {
                    last.texts.push(seg.text);
                    last.lastTs = seg.ts;
                  } else {
                    groups.push({ isDoctor, label, texts: [seg.text], key: seg.id, lastTs: seg.ts });
                  }
                }
                return groups.map((g) => (
                  <div key={g.key} className={g.isDoctor ? styles.txRowDoctor : styles.txRowPatient}>
                    <span className={styles.txSpeaker}>{g.label}</span>
                    <div className={styles.txTextBlock}>
                      <p className={styles.txMsg}>{g.texts.join(' ')}</p>
                    </div>
                    <span className={styles.txTime}>{fmtTime(g.lastTs)}</span>
                  </div>
                ));
              })()}
            </div>
          </section>
        )}

        {/* Empty hint si todo está vacío */}
        {!hasSoap && fhirConsolidado.length === 0 && transcriptSegments.length === 0 && notasMedicoTexto.length === 0 && (
          <section className={styles.section}>
            <p className={styles.muted}>No hay información clínica adicional registrada para esta consulta.</p>
          </section>
        )}

        {/* Botón flotante de agregar nota (modo enfoque) */}
        {ventana.puedeAgregar && (
          <button
            type="button"
            className={styles.fab}
            onClick={() => setShowNotaModal(true)}
            title={`Agregar nota (${ventana.horasRestantes}h restantes)`}
            aria-label="Agregar nota a la consulta"
          >
            <Plus size={20} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Modal: agregar nota post-consulta */}
      <Modal
        open={showNotaModal}
        onClose={() => { if (!notaSubmitting) setShowNotaModal(false); }}
        title="Agregar nota a la consulta"
      >
        <div className={styles.modalBody}>
          <div className={styles.modalContext}>
            <p><strong>Paciente:</strong> {fixMojibake(consulta.pacienteNombre)}</p>
            <p><strong>Motivo:</strong> {consulta.motivoConsulta}</p>
            <p><strong>Tiempo restante:</strong> {ventana.horasRestantes}h</p>
            <p className={styles.modalWarn}>
              <AlertTriangle size={11} aria-hidden="true" />
              Las notas son inmutables una vez guardadas. No podrá editarlas ni eliminarlas.
            </p>
          </div>
          <label className={styles.modalLabel}>Nueva nota</label>
          <textarea
            className={styles.modalTextarea}
            value={notaTexto}
            onChange={(e) => setNotaTexto(e.target.value.slice(0, 5000))}
            placeholder="Anote información adicional sobre la consulta..."
            rows={6}
            disabled={notaSubmitting}
          />
          <p className={styles.charCount}>{notaTexto.length} / 5000</p>
          <div className={styles.modalActions}>
            <Button variant="ghost" onClick={() => setShowNotaModal(false)} disabled={notaSubmitting}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitNota}
              loading={notaSubmitting}
              disabled={!notaTexto.trim() || notaSubmitting}
            >
              Guardar nota
            </Button>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
}
